/**
 * When a hidden editor panel may keep the tree it already built.
 *
 * The playEngine editors keep every panel mounted and hand React back the same
 * element object while a tab is off screen, so an edit made in one panel stops
 * re-rendering the other seven. Freezing an element also withholds its props,
 * though, and that is only safe for props the panel merely *displays* — those
 * it picks up when the tab is opened and the body is rebuilt.
 *
 * It is not safe for props the panel *acts* on while hidden, in a window that
 * has closed by the time anyone opens the tab. Version Settings clears its
 * component tree when the selected version changes and refills it from the
 * engine's one-shot `playengine:playable-ready`; frozen on the old version it
 * never learns of the change, lets the announcement pass as "nothing to do",
 * and then clears itself when opened — the tab reads "No components found"
 * until some later switch happens to catch it live. Undo/redo in the snapshot
 * editor is the same shape.
 *
 * The rule that follows: freeze against edits, not against identity. Callers
 * name the identity in a `generation` string, and a cached body built under a
 * different one is rebuilt even while hidden.
 */

/**
 * @param {object}  options
 * @param {?object} options.cached        the cache entry, or null if never built
 * @param {string}  options.generation    identity the body must have been built under
 * @param {boolean} options.isActive      this tab is the open one
 * @param {boolean} [options.isLive]      never frozen (the agent panels)
 * @param {boolean} [options.mountEagerly] build on first render even while hidden
 * @returns {boolean} true if the caller must render a fresh body
 */
export function shouldRebuildPanel({
  cached,
  generation,
  isActive,
  isLive = false,
  mountEagerly = false,
}) {
  if (isActive || isLive) return true;
  // Never built. Lazy mounting has the freeze's blind spot one step earlier: a
  // panel that is not mounted has no listener to miss the engine's
  // announcement with. Panels that recover on their own — they read
  // `isPreviewLoaded`, a prop still true whenever they are opened — stay lazy.
  if (!cached) return mountEagerly;
  return cached.generation !== generation;
}

export default shouldRebuildPanel;
